/**
 * Portal-owned Monday trigger events — the "Events" tab of the Mirror Autopilot dialog.
 * Decline Send / Request Info / Chase IR clicks on Monday now land here instead of Make:
 * per-handler mode (off · shadow · live), the broker-safe decline label list, webhook +
 * poll health, and the event log with approve / dismiss for parked sends.
 */
import { useState, useEffect, useCallback } from "react";
import { Loader2, Check, X, RefreshCw, Eye, Webhook, Radio, ShieldAlert, GitCompare, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  mirrorEventsStatus, mirrorEventsSettings, mirrorEventsSaveSettings, mirrorEventsList,
  mirrorEventsApprove, mirrorEventsDismiss, mirrorEventsPreview, mirrorEventsPollNow,
  mirrorEventsRegisterWebhooks, mirrorEventsRemoveWebhooks, mirrorEventsCompare,
} from "@/lib/api";

const KIND_LABEL = { decline_send: "Decline email", info_request: "Information request", ir_chase: "IR chase" };
const MODE_CLS = { off: "bg-slate-100 text-slate-600", shadow: "bg-amber-100 text-amber-800", live: "bg-emerald-100 text-emerald-800" };
const STATUS_CLS = {
  sent: "bg-emerald-100 text-emerald-800", would_send: "bg-sky-100 text-sky-800", blocked: "bg-rose-100 text-rose-800",
  needs_review: "bg-amber-100 text-amber-800", failed: "bg-rose-200 text-rose-900", skipped: "bg-slate-100 text-slate-600",
  dismissed: "bg-slate-100 text-slate-500", processing: "bg-violet-100 text-violet-800",
};
const OPEN = ["needs_review", "blocked", "failed"];

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

export function EventsTab() {
  const [status, setStatus] = useState(null);
  const refresh = useCallback(() => mirrorEventsStatus().then(setStatus).catch(() => toast.error("Failed to load events status")), []);
  useEffect(() => { refresh(); }, [refresh]);
  if (!status) return <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto my-6" />;
  return (
    <div className="space-y-3" data-testid="mirror-events-tab">
      <HandlerModes status={status} refresh={refresh} />
      <Sources status={status} refresh={refresh} />
      <ContentMode status={status} refresh={refresh} />
      <DeclineLabels />
      <CompareMake />
      <EventLog refresh={refresh} openCount={status.open} />
    </div>
  );
}

const ENHANCED_CHANGES = [
  ["Incomplete Application", "the email carries the underwriter's (INC) note — what arrived, what is missing, the Open Banking link — and says the file moves to Declined for now and goes back through on receipt"],
  ["Specific reason", "\"Didn't meet the eligibility criteria\" is a wrapper — the broker is told the specific label next to it (No affordability, Minimum Sales…), never the wrapper alone"],
  ["Sole Trader", "adds \"We lend to limited companies and LLPs only.\""],
  ["Signature", "drops the inline Applications Team block — the server signature already signs every email, so Make's emails signed twice"],
  ["Unchanged", "subjects, recipients, the Monday note, the status moves — and every IR chase still quotes what is outstanding"],
];

function ContentMode({ status, refresh }) {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const mode = status.settings.content_mode || "parity";
  const set = async (v) => {
    if (v === mode) return;
    setSaving(true);
    try { await mirrorEventsSaveSettings({ content_mode: v, actor: user?.name || "Ops" }); toast.success(v === "enhanced" ? "Enhanced content on — brokers now get the reason" : "Back to Make-identical content"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="border rounded-lg p-3 space-y-2" data-testid="mirror-events-content-mode">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold text-slate-800 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-violet-600" /> What the email says</div>
          <div className="text-[11px] text-slate-500">
            <b>Parity</b> = word-for-word what Make sent, so the shadow comparison stays clean. <b>Enhanced</b> = Phase 1 content: the reason behind every decline, the (INC) note on incomplete files, one signature.
            Switch to Enhanced once the handler is live and Make is off.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={`text-[10px] border-0 ${mode === "enhanced" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`} data-testid="mirror-events-content-badge">{mode}</Badge>
          <Select value={mode} onValueChange={set} disabled={saving}>
            <SelectTrigger className="h-7 text-xs w-44" data-testid="mirror-events-content-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="parity">Parity — as Make sent it</SelectItem>
              <SelectItem value="enhanced">Enhanced — with the reason</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setOpen(o => !o)} data-testid="mirror-events-content-toggle">{open ? "Hide" : "What changes"}</Button>
        </div>
      </div>
      {open && (
        <ul className="text-[11px] text-slate-600 space-y-0.5 pl-1" data-testid="mirror-events-content-changes">
          {ENHANCED_CHANGES.map(([k, v]) => <li key={k}><span className="font-medium text-slate-800">{k}:</span> {v}</li>)}
        </ul>
      )}
    </div>
  );
}

function CompareMake() {
  const [hours, setHours] = useState(24);
  const [busy, setBusy] = useState(false);
  const [r, setR] = useState(null);
  const run = async () => {
    setBusy(true);
    try { setR(await mirrorEventsCompare(hours)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Compare failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="border rounded-lg p-3 space-y-2" data-testid="mirror-events-compare">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-semibold text-slate-800 flex items-center gap-1.5"><GitCompare className="w-3.5 h-3.5 text-violet-600" /> Shadow check — what Make sent vs what the portal would have sent</div>
          <div className="text-[11px] text-slate-500">Reads applications@ Sent Items and lines each Make email up with the portal event for the same deal. Clean = same recipients, subject and opening text. Run this for a week before switching Make off.</div>
        </div>
        <div className="flex items-center gap-1.5">
          <Input type="number" min={1} max={336} value={hours} onChange={(e) => setHours(Number(e.target.value) || 24)} className="h-7 text-xs w-16" data-testid="mirror-events-compare-hours" />
          <span className="text-[11px] text-slate-500">h</span>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={run} disabled={busy} data-testid="mirror-events-compare-run">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Compare"}</Button>
        </div>
      </div>
      {r && (
        <div className="space-y-1.5" data-testid="mirror-events-compare-result">
          <div className="text-[11px] text-slate-700">
            Make sent <b>{r.make_sent}</b> · portal logged <b>{r.portal_events}</b> · matched <b>{r.matched}</b> ·
            <span className="text-emerald-700 font-medium"> {r.matched_clean} identical</span> ·
            <span className="text-amber-700"> {r.with_diffs.length} differ</span> ·
            <span className="text-rose-700"> {r.make_only.length} Make-only</span> ·
            <span className="text-sky-700"> {r.portal_only.length} portal-only</span>
          </div>
          {r.with_diffs.slice(0, 40).map((m) => (
            <div key={m.event_id} className="text-[11px] border-l-2 border-amber-300 pl-2"><b>{KIND_LABEL[m.kind]}</b> · {m.item_name} — {m.diffs.join(" · ")}</div>
          ))}
          {r.make_only.slice(0, 40).map((m) => (
            <div key={m.id} className="text-[11px] border-l-2 border-rose-300 pl-2"><b>Make only</b> · {m.subject} → {m.to.join(", ")} · {fmt(m.at)}</div>
          ))}
          {r.portal_only.slice(0, 40).map((m) => (
            <div key={m.event_id} className="text-[11px] border-l-2 border-sky-300 pl-2"><b>Portal only</b> · {KIND_LABEL[m.kind]} · {m.item_name} · {m.status.replace("_", " ")}{m.reason ? ` — ${m.reason}` : ""}</div>
          ))}
          {(r.with_diffs.length + r.make_only.length + r.portal_only.length) > 120 && (
            <div className="text-[10px] text-slate-500">Showing the first 40 of each list.</div>
          )}
        </div>
      )}
    </div>
  );
}

function HandlerModes({ status, refresh }) {
  const { user } = useAuth();
  const [handlers, setHandlers] = useState(status.settings.handlers);
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(handlers) !== JSON.stringify(status.settings.handlers);
  const save = async () => {
    if (Object.values(handlers).includes("live") && !window.confirm(
      "LIVE means the portal sends the email and makes the Monday writes itself. Make sure the matching Make scenario is OFF first — otherwise the broker gets two emails. Continue?")) return;
    setSaving(true);
    try { await mirrorEventsSaveSettings({ handlers, actor: user?.name || "Ops" }); toast.success("Handler modes saved"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const counts = status.counts_7d || {};
  return (
    <div className="border rounded-lg p-3 space-y-2" data-testid="mirror-events-handlers">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold text-slate-800 flex items-center gap-1.5"><Radio className="w-3.5 h-3.5 text-violet-600" /> Who sends when a trigger is clicked</div>
          <div className="text-[11px] text-slate-500">
            <b>Off</b> = Make still owns it · <b>Shadow</b> = portal composes and logs what it would have sent, nothing goes out · <b>Live</b> = portal sends from applications@ and makes the same Monday writes Make did. One sender per state — switch Make off before going live.
          </div>
        </div>
        {dirty && <Button size="sm" className="h-7 text-xs" onClick={save} disabled={saving} data-testid="mirror-events-save-modes">{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}</Button>}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {Object.entries(KIND_LABEL).map(([k, label]) => {
          const c = counts[k] || {};
          return (
            <div key={k} className="border rounded-md p-2 space-y-1.5" data-testid={`mirror-events-handler-${k}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-slate-700">{label}</span>
                <Badge variant="outline" className={`text-[10px] border-0 ${MODE_CLS[handlers[k]]}`}>{handlers[k]}</Badge>
              </div>
              <Select value={handlers[k]} onValueChange={(v) => setHandlers(p => ({ ...p, [k]: v }))}>
                <SelectTrigger className="h-7 text-xs" data-testid={`mirror-events-mode-${k}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off — Make owns it</SelectItem>
                  <SelectItem value="shadow">Shadow — log only</SelectItem>
                  <SelectItem value="live">Live — portal sends</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-[10px] text-slate-500">
                7 days: {Object.entries(c).length ? Object.entries(c).map(([s, n]) => `${n} ${s.replace("_", " ")}`).join(" · ") : "no events yet"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Sources({ status, refresh }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState("");
  const hooks = status.webhooks || [];
  const run = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); toast.success(typeof ok === "function" ? ok(r) : ok); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };
  const lp = status.last_poll;
  return (
    <div className="border rounded-lg p-3 grid grid-cols-2 gap-3" data-testid="mirror-events-sources">
      <div className="space-y-1">
        <div className="text-xs font-semibold text-slate-800 flex items-center gap-1.5"><Webhook className="w-3.5 h-3.5 text-violet-600" /> Monday webhook</div>
        <div className="text-[11px] text-slate-500">Instant delivery on the Decline Send and Request Info columns. Same mechanism Make uses.</div>
        <div className="text-[11px]" data-testid="mirror-events-webhook-state">
          {hooks.length ? <span className="text-emerald-700">{hooks.length} registered ({hooks.map(h => COL_NAME[h.column_id] || h.column_id).join(", ")})</span>
            : <span className="text-amber-700">not registered — the 60-s poll is carrying events</span>}
          {status.last_webhook_event && <span className="text-slate-500"> · last delivery {fmt(status.last_webhook_event.created_at)} ({status.last_webhook_event.item_name})</span>}
        </div>
        {status.other_webhooks?.length > 0 && (
          <div className="text-[11px] text-slate-500" data-testid="mirror-events-other-webhooks">
            {status.other_webhooks.length} other webhook(s) on these columns — Make's. They stay until the Make scenarios are switched off; Remove here never touches them.
          </div>
        )}
        <div className="flex gap-2">
          {hooks.length < 2 && <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy === "reg"}
            onClick={() => run("reg", () => mirrorEventsRegisterWebhooks(user?.name || "Ops"), (r) => `Webhook registered (${r.created?.length || 0} new)`)}
            data-testid="mirror-events-register-webhook">{busy === "reg" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Register webhook"}</Button>}
          {hooks.length > 0 && <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" disabled={busy === "rm"}
            onClick={() => window.confirm("Remove the portal's Monday webhooks? Events fall back to the 60-second poll.") && run("rm", () => mirrorEventsRemoveWebhooks(user?.name || "Ops"), "Webhooks removed")}
            data-testid="mirror-events-remove-webhook">Remove</Button>}
        </div>
      </div>
      <div className="space-y-1">
        <div className="text-xs font-semibold text-slate-800 flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5 text-violet-600" /> 60-second poll</div>
        <div className="text-[11px] text-slate-500">Reads the Monday activity log for the trigger columns — catches anything the webhook missed. Same event seen twice = one event.</div>
        <div className="text-[11px] text-slate-600" data-testid="mirror-events-poll-state">
          {lp ? <>last {fmt(lp.at)} · {lp.trigger_events} trigger change(s), {lp.new_events} new</> : "not run yet"}
        </div>
        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy === "poll"}
          onClick={() => run("poll", mirrorEventsPollNow, (r) => r.skipped ? `Poll skipped — ${r.skipped}` : `Polled: ${r.trigger_events} trigger change(s), ${r.new_events} new`)}
          data-testid="mirror-events-poll-now">{busy === "poll" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Poll now"}</Button>
      </div>
    </div>
  );
}

function DeclineLabels() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { mirrorEventsSettings().then(setS).catch(() => {}); }, []);
  if (!s) return null;
  const allowed = new Set(s.allowed_decline_labels);
  const options = Array.from(new Set([...(s.decline_label_options || []), ...s.allowed_decline_labels]));
  const toggle = (l) => setS(p => ({ ...p, allowed_decline_labels: allowed.has(l) ? p.allowed_decline_labels.filter(x => x !== l) : [...p.allowed_decline_labels, l] }));
  const save = async () => {
    setSaving(true);
    try { const r = await mirrorEventsSaveSettings({ allowed_decline_labels: s.allowed_decline_labels, actor: user?.name || "Ops" }); setS(p => ({ ...p, ...r })); toast.success("Decline label policy saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="border rounded-lg p-3 space-y-2" data-testid="mirror-events-decline-labels">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold text-slate-800 flex items-center gap-1.5"><ShieldAlert className="w-3.5 h-3.5 text-rose-600" /> Reasons a broker may be told</div>
          <div className="text-[11px] text-slate-500">
            A decline whose reason isn't ticked is parked, not sent (Make had no such check — "On Hold - Midpoint" and "Not Endorsed by the Credit Team" reached brokers). {allowed.size} of {options.length} labels allowed.
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setOpen(o => !o)} data-testid="mirror-events-labels-toggle">{open ? "Hide" : "Edit list"}</Button>
          {open && <Button size="sm" className="h-7 text-xs" onClick={save} disabled={saving} data-testid="mirror-events-labels-save">{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}</Button>}
        </div>
      </div>
      {open && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 max-h-56 overflow-y-auto pr-1">
          {options.map((l) => (
            <label key={l} className="flex items-start gap-1.5 text-[11px] cursor-pointer">
              <input type="checkbox" className="mt-0.5" checked={allowed.has(l)} onChange={() => toggle(l)} data-testid={`mirror-events-label-${l.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />
              <span className={allowed.has(l) ? "text-slate-800" : "text-slate-400 line-through"}>{l}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function EventLog({ refresh, openCount }) {
  const { user } = useAuth();
  const [view, setView] = useState("open");
  const [events, setEvents] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [previewId, setPreviewId] = useState("");
  const [previewKind, setPreviewKind] = useState("decline_send");
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    try { setEvents((await mirrorEventsList(view === "all" ? "" : view)).events); }
    catch { toast.error("Failed to load events"); }
  }, [view]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, id, ok) => {
    setBusyId(id);
    try { await fn(id, user?.name || "Ops"); toast.success(ok); await load(); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusyId(null); }
  };
  const runPreview = async () => {
    if (!previewId.trim()) return;
    setBusyId("preview");
    try { setPreview(await mirrorEventsPreview(previewId.trim(), previewKind)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="border rounded-lg p-3 space-y-2" data-testid="mirror-events-log">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1">
          {[["open", `Needs attention${openCount ? ` (${openCount})` : ""}`], ["sent", "Sent"], ["would_send", "Would send"], ["all", "All"]].map(([k, label]) => (
            <button key={k} onClick={() => setView(k)}
              className={`px-2 py-1 text-[11px] rounded-md ${view === k ? "bg-violet-100 text-violet-800 font-medium" : "text-slate-500 hover:bg-slate-100"}`}
              data-testid={`mirror-events-view-${k}`}>{label}</button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <Input value={previewId} onChange={(e) => setPreviewId(e.target.value)} placeholder="Monday item id" className="h-7 text-xs w-32" data-testid="mirror-events-preview-item" />
          <Select value={previewKind} onValueChange={setPreviewKind}>
            <SelectTrigger className="h-7 text-xs w-40" data-testid="mirror-events-preview-kind"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(KIND_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={runPreview} disabled={busyId === "preview"} data-testid="mirror-events-preview-run">
            {busyId === "preview" ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Eye className="w-3 h-3 mr-1" /> Dry run</>}
          </Button>
        </div>
      </div>

      {preview && (
        <div className="border border-sky-200 bg-sky-50/50 rounded-md p-2 space-y-1" data-testid="mirror-events-preview-result">
          <div className="flex items-center justify-between">
            <div className="text-[11px]"><b>Dry run</b> · {preview.item_name} · {KIND_LABEL[preview.kind]} · status now "{preview.status_now}" · trigger "{preview.trigger_now || "—"}"
              <Badge variant="outline" className={`ml-2 text-[10px] border-0 ${STATUS_CLS[preview.status] || ""}`}>{preview.status.replace("_", " ")}</Badge></div>
            <button className="text-[11px] text-slate-500" onClick={() => setPreview(null)}>close</button>
          </div>
          {preview.reason && <div className="text-[11px] text-rose-700">{preview.reason}</div>}
          <MailCard mail={preview.mail} writes={preview.planned_writes} />
        </div>
      )}

      {!events ? <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto my-4" />
        : events.length === 0 ? <div className="text-[11px] text-slate-500 text-center py-4" data-testid="mirror-events-empty">Nothing here.</div>
        : (
          <div className="space-y-1.5">
            {events.map((ev) => {
              const isOpen = OPEN.includes(ev.status);
              const canApprove = isOpen && ev.mail?.to?.length > 0 && !(ev.kind === "ir_chase" && ev.ctx?.step === null && ev.status === "needs_review");
              return (
                <div key={ev.event_id} className={`border rounded-md p-2 ${isOpen ? "border-amber-200 bg-amber-50/30" : ""}`} data-testid={`mirror-event-${ev.event_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Badge variant="outline" className={`text-[10px] border-0 ${STATUS_CLS[ev.status] || "bg-slate-100"}`} data-testid={`mirror-event-status-${ev.event_id}`}>{ev.status.replace("_", " ")}</Badge>
                        <span className="text-[11px] font-medium text-slate-800">{KIND_LABEL[ev.kind]}</span>
                        <Badge variant="outline" className={`text-[10px] border-0 ${MODE_CLS[ev.mode]}`}>{ev.mode}</Badge>
                        <span className="text-[11px] text-slate-700 truncate">{ev.item_name || ev.item_id}</span>
                      </div>
                      <div className="text-[10px] text-slate-500">
                        {fmt(ev.trigger_at)} · trigger "{ev.label}" via {(ev.sources || [ev.source]).join("/")}{ev.deliveries > 1 ? ` ×${ev.deliveries}` : ""}
                        {ev.mail?.to?.length ? <> · to {ev.mail.to.join(", ")}</> : null}
                        {ev.actor ? <> · {ev.actor}</> : null}
                      </div>
                      {ev.reason && <div className="text-[11px] text-rose-700 mt-0.5">{ev.reason}</div>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {ev.mail?.subject && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setExpanded(expanded === ev.event_id ? null : ev.event_id)} data-testid={`mirror-event-expand-${ev.event_id}`}><Eye className="w-3.5 h-3.5" /></Button>}
                      {canApprove && <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-emerald-700" disabled={busyId === ev.event_id}
                        onClick={() => window.confirm(`Send the ${KIND_LABEL[ev.kind].toLowerCase()} for ${ev.item_name} now?`) && act(mirrorEventsApprove, ev.event_id, "Sent")}
                        data-testid={`mirror-event-approve-${ev.event_id}`}><Check className="w-3.5 h-3.5 mr-1" /> Send</Button>}
                      {isOpen && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-slate-500" disabled={busyId === ev.event_id}
                        onClick={() => act(mirrorEventsDismiss, ev.event_id, "Dismissed")} data-testid={`mirror-event-dismiss-${ev.event_id}`}><X className="w-3.5 h-3.5" /></Button>}
                    </div>
                  </div>
                  {expanded === ev.event_id && <div className="mt-2"><MailCard mail={ev.mail} writes={ev.monday_writes || ev.planned_writes} /></div>}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}

const COL_NAME = { color_mkqhpvgy: "Decline Send", color_mkqxr56s: "Request Info", status: "Status", long_text_mkzwf50s: "Information Request", date_mkzwt3jv: "Date Last IR Chased" };

function MailCard({ mail, writes }) {
  if (!mail?.subject) return null;
  return (
    <div className="border rounded-md bg-white p-2 space-y-1" data-testid="mirror-event-mail">
      <div className="text-[11px]"><b>To:</b> {(mail.to || []).join(", ") || <span className="text-rose-700">none</span>}{mail.bcc?.length ? <> · <b>Bcc:</b> {mail.bcc.join(", ")}</> : null}</div>
      <div className="text-[11px]"><b>Subject:</b> {mail.subject}</div>
      <div className="text-[11px] border rounded p-2 bg-slate-50 max-h-48 overflow-y-auto prose-sm" dangerouslySetInnerHTML={{ __html: mail.html || "" }} />
      {writes?.length > 0 && (
        <div className="text-[10px] text-slate-500">
          Monday writes: {writes.map((w, i) => <span key={i} className="mr-2">{w.op === "cols" ? Object.entries(w.arg).map(([c, v]) => `${COL_NAME[c] || c} → ${v.label || v.text?.slice(0, 30) || v.date || ""}`).join(", ") : `note "${String(w.arg).slice(0, 50)}…"`}</span>)}
        </div>
      )}
    </div>
  );
}
